import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { invoicesService } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardTitle, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input, Textarea, Select } from '../../components/UI/Input';
import { Spinner } from '../../components/UI/Spinner';
import { Modal } from '../../components/UI/Modal';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import {
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  UserPlus,
  Sparkles,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  ChevronDown,
  AlertCircle,
  DollarSign,
} from 'lucide-react';

export const InvoiceForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  // Reference data
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [nextNumber, setNextNumber] = useState('000001');

  // Edit-specific state
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState(0);
  const [payments, setPayments] = useState([]);
  const [amountPending, setAmountPending] = useState('0.00');

  // Status dropdown in edit mode
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [pendingWarningOpen, setPendingWarningOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Payment modal state in edit mode
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('Cash');
  const [payDescription, setPayDescription] = useState('');
  const [paySubmitting, setPaySubmitting] = useState(false);

  // Delete payment state
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [deletePaymentConfirmOpen, setDeletePaymentConfirmOpen] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    invo_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    customer_id: '',
    project_id: '',
    currency_id: '1',
    notes: '',
    is_new_customer: false,
    customer_name: '',
    customer_email: '',
    customer_address1: '',
    customer_address2: '',
  });

  // Items State
  const [items, setItems] = useState([
    { product_id: '', description: '', quantity: 1, price: 0, total: 0 }
  ]);

  const [errors, setErrors] = useState({});

  // Close status dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const refreshPayments = useCallback(async () => {
    if (!id) return;
    try {
      const editRes = await invoicesService.get(id);
      if (editRes && editRes.data) {
        setPayments(editRes.data.payments || []);
        setAmountPending(editRes.data.amount_pending || '0.00');
        if (editRes.data.invoice) {
          setInvoiceStatus(editRes.data.invoice.status);
          setInvoiceNumber(editRes.data.invoice.number || '');
        }
      }
    } catch (err) {
      console.error('Error al recargar pagos:', err);
    }
  }, [id]);

  useEffect(() => {
    const loadFormData = async () => {
      setLoading(true);
      try {
        const createRes = await invoicesService.getCreateData();
        if (createRes && createRes.data) {
          if (createRes.data.limitReached) {
            setLimitReached(true);
            setLoading(false);
            return;
          }

          setCustomers(createRes.data.customers || []);
          setProjects(createRes.data.projects || []);
          setProducts(createRes.data.products || []);
          setCurrencies(createRes.data.currencies || []);
          if (createRes.data.nextNumber) setNextNumber(createRes.data.nextNumber);
          
          if (createRes.data.currencies?.length > 0) {
            setFormData(prev => ({
              ...prev,
              currency_id: prev.currency_id || String(createRes.data.currencies[0].id)
            }));
          }
        }

        if (isEdit) {
          const editRes = await invoicesService.get(id);
          if (editRes && editRes.data && editRes.data.invoice) {
            const inv = editRes.data.invoice;
            setInvoiceNumber(inv.number || '');
            setInvoiceStatus(inv.status);
            setPayments(editRes.data.payments || []);
            setAmountPending(editRes.data.amount_pending || '0.00');

            setFormData({
              invo_date: inv.invo_date || '',
              due_date: inv.due_date || '',
              customer_id: String(inv.customer_id || ''),
              project_id: inv.project_id ? String(inv.project_id) : '',
              currency_id: String(inv.currency_id || '1'),
              notes: inv.notes || '',
              is_new_customer: false,
              customer_name: '',
              customer_email: '',
              customer_address1: '',
              customer_address2: '',
            });

            if (inv.invoiceItems && Array.isArray(inv.invoiceItems) && inv.invoiceItems.length > 0) {
              setItems(inv.invoiceItems.map(item => ({
                product_id: item.product_id ? String(item.product_id) : '',
                description: item.description || '',
                quantity: Number(item.quantity || 1),
                price: Number(item.price || 0),
                total: Number(item.price || 0) * Number(item.quantity || 1)
              })));
            }
          }
        }
      } catch (err) {
        toastError(err.message || 'Error al cargar datos del formulario.');
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, [id, isEdit, toastError]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;

    if (field === 'product_id') {
      const selectedProd = products.find(p => String(p.id) === String(value));
      if (selectedProd) {
        updated[index].description = selectedProd.name;
        updated[index].price = Number(selectedProd.price || 0);
      }
    }

    const qty = parseFloat(updated[index].quantity) || 0;
    const prc = parseFloat(updated[index].price) || 0;
    updated[index].total = Number((qty * prc).toFixed(2));

    setItems(updated);
  };

  const addItemRow = () => {
    setItems(prev => [
      ...prev,
      { product_id: '', description: '', quantity: 1, price: 0, total: 0 }
    ]);
  };

  const removeItemRow = (index) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculateGrandTotal = () => {
    return items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  };

  // Status Change in Edit Mode
  const handleStatusChange = async (newStatus) => {
    setStatusDropdownOpen(false);
    if (Number(invoiceStatus) === Number(newStatus)) return;

    if (Number(newStatus) === 1 && Number(amountPending) > 0.01) {
      setPendingWarningOpen(true);
      return;
    }

    setStatusSubmitting(true);
    try {
      await invoicesService.changeStatus(id, newStatus);
      const statusLabels = { 0: 'Abierta', 1: 'Pagada', 2: 'Cerrada' };
      success(`Factura marcada como ${statusLabels[newStatus]}.`);
      await refreshPayments();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al cambiar estado.');
    } finally {
      setStatusSubmitting(false);
    }
  };

  // Open Payment Modal in Edit Mode
  const openPayModal = () => {
    setPayAmount(Number(amountPending) > 0 ? Number(amountPending).toFixed(2) : '');
    setPayMethod('Cash');
    setPayDescription('');
    setPayModalOpen(true);
  };

  // Submit Payment in Edit Mode
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    setPaySubmitting(true);
    try {
      await invoicesService.pay(id, {
        amount: payAmount,
        method: payMethod,
        description: payDescription,
      });
      success('Abono registrado correctamente.');
      setPayModalOpen(false);
      setPayDescription('');
      await refreshPayments();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al registrar abono.');
    } finally {
      setPaySubmitting(false);
    }
  };

  // Delete a Payment in Edit Mode
  const handleDeletePayment = async () => {
    if (!paymentToDelete) return;
    try {
      await invoicesService.deletePayment(id, paymentToDelete.id);
      success('Abono eliminado e importe recalculado.');
      setDeletePaymentConfirmOpen(false);
      setPaymentToDelete(null);
      await refreshPayments();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar pago.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    const total = calculateGrandTotal();

    const payload = {
      invo_date: formData.invo_date,
      due_date: formData.due_date,
      project_id: formData.project_id ? formData.project_id : null,
      currency_id: formData.currency_id,
      notes: formData.notes,
      items: JSON.stringify(items),
      total: total,
      customer_id: formData.is_new_customer ? 'new' : formData.customer_id,
      ...(formData.is_new_customer ? {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_address1: formData.customer_address1,
        customer_address2: formData.customer_address2,
      } : {})
    };

    try {
      if (isEdit) {
        await invoicesService.update(id, payload);
        success('Factura actualizada correctamente.');
      } else {
        await invoicesService.create(payload);
        success('Factura emitida exitosamente.');
      }
      navigate('/invoices');
    } catch (err) {
      if (err.errors) {
        setErrors(err.errors);
      }
      toastError(err.data?.message || err.message || 'Error al guardar factura.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Spinner size="lg" label="Cargando formulario..." className="py-20" />;
  }

  if (limitReached) {
    return (
      <div className="max-w-xl mx-auto py-12 text-center space-y-6">
        <div className="p-8 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-amber-50 dark:bg-amber-950 text-amber-600 flex items-center justify-center mx-auto">
            <Sparkles className="w-7 h-7" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            Límite del Plan Gratuito Alcanzado
          </h2>
          <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed max-w-md mx-auto">
            Has emitido el máximo de facturas permitidas en la versión gratuita (5 facturas). Suscríbete al Plan Pro para facturar sin límites.
          </p>
          <div className="pt-2 flex items-center justify-center gap-3">
            <Link to="/invoices">
              <Button variant="secondary" size="sm">Volver a Facturas</Button>
            </Link>
            <Link to="/subscriptions">
              <Button variant="primary" size="sm" className="bg-[#08707A] hover:bg-[#065B63]">
                Ver Planes de Suscripción
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const grandTotal = calculateGrandTotal();
  const selectedCurrency = currencies.find(c => String(c.id) === String(formData.currency_id));
  const currencySymbol = selectedCurrency?.symbol || '$';
  const totalPaid = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const pctPaid = grandTotal > 0 ? Math.min(100, Math.round((totalPaid / grandTotal) * 100)) : 0;

  const getStatusBadgeConfig = (status) => {
    switch (Number(status)) {
      case 1:
        return {
          label: 'Pagada',
          icon: CheckCircle,
          className: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800',
        };
      case 2:
        return {
          label: 'Cerrada',
          icon: XCircle,
          className: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
        };
      default:
        return {
          label: 'Abierta',
          icon: Clock,
          className: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800',
        };
    }
  };

  const statusConfig = getStatusBadgeConfig(invoiceStatus);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/invoices">
            <Button variant="ghost" size="sm" icon={ArrowLeft}>
              Volver
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight font-mono">
                {isEdit ? `Editar Factura #${invoiceNumber || id}` : `Nueva Factura (${nextNumber})`}
              </h1>

              {/* Status Interactive Selector in Edit Mode */}
              {isEdit && (
                <div className="relative inline-block text-left" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                    disabled={statusSubmitting}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-lg border shadow-sm transition-all hover:opacity-90 ${statusConfig.className} ${statusSubmitting ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                    title="Click para cambiar el estado de la factura"
                  >
                    <StatusIcon className="w-3.5 h-3.5" />
                    <span>{statusConfig.label}</span>
                    <ChevronDown className="w-3 h-3 opacity-60 ml-0.5" />
                  </button>

                  {/* Dropdown Menu */}
                  {statusDropdownOpen && (
                    <div className="absolute left-0 mt-1.5 w-40 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-800 py-1.5 z-30 animate-in fade-in zoom-in-95 duration-100">
                      <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 dark:border-slate-800">
                        Cambiar Estado
                      </div>
                      <button
                        type="button"
                        onClick={() => handleStatusChange(0)}
                        className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-amber-50 dark:hover:bg-amber-950/40 transition-colors ${Number(invoiceStatus) === 0 ? 'text-amber-600 font-bold bg-amber-50/50 dark:bg-amber-950/20' : 'text-slate-700 dark:text-slate-300'}`}
                      >
                        <Clock className="w-3.5 h-3.5 text-amber-500" />
                        Abierta
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusChange(1)}
                        className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-colors ${Number(invoiceStatus) === 1 ? 'text-emerald-600 font-bold bg-emerald-50/50 dark:bg-emerald-950/20' : 'text-slate-700 dark:text-slate-300'}`}
                      >
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                        Pagada
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusChange(2)}
                        className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${Number(invoiceStatus) === 2 ? 'text-slate-900 dark:text-white font-bold bg-slate-100 dark:bg-slate-800' : 'text-slate-700 dark:text-slate-300'}`}
                      >
                        <XCircle className="w-3.5 h-3.5 text-slate-500" />
                        Cerrada
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {isEdit ? 'Modifica los datos del comprobante fiscal, gestiona pagos y abonos' : 'Emite un comprobante formal para tu cliente'}
            </p>
          </div>
        </div>

        {/* Quick Payment Action Button in Edit Header */}
        {isEdit && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="primary"
              size="sm"
              icon={CreditCard}
              onClick={openPayModal}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm"
            >
              {Number(amountPending) <= 0 ? 'Registrar Abono' : 'Registrar Pago / Abono'}
            </Button>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Main Details Card */}
        <Card>
          <CardHeader>
            <CardTitle subtitle="Información general y plazos de pago">
              Encabezado de la Factura
            </CardTitle>
          </CardHeader>

          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Input
                label="Fecha de Emisión"
                type="date"
                name="invo_date"
                value={formData.invo_date}
                onChange={handleInputChange}
                error={errors.invo_date?.[0]}
                required
              />

              <Input
                label="Fecha de Vencimiento"
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleInputChange}
                error={errors.due_date?.[0]}
              />

              <Select
                label="Moneda"
                name="currency_id"
                value={formData.currency_id}
                onChange={handleInputChange}
                error={errors.currency_id?.[0]}
                required
              >
                {currencies.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.code} ({c.symbol}) - {c.name}
                  </option>
                ))}
              </Select>

              <Select
                label="Proyecto (Opcional)"
                name="project_id"
                value={formData.project_id}
                onChange={handleInputChange}
                error={errors.project_id?.[0]}
              >
                <option value="">-- Sin Proyecto Asociado --</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </div>

            {/* Customer Section */}
            <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Cliente Destinatario
                </label>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, is_new_customer: !prev.is_new_customer }))}
                  className="text-xs text-[#08707A] dark:text-teal-400 font-bold hover:underline flex items-center gap-1"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  {formData.is_new_customer ? 'Seleccionar Cliente Existente' : '+ Registrar Cliente Nuevo Aquí'}
                </button>
              </div>

              {!formData.is_new_customer ? (
                <Select
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleInputChange}
                  error={errors.customer_id?.[0]}
                  required={!formData.is_new_customer}
                >
                  <option value="">-- Selecciona un Cliente del Directorio --</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.full_name} {c.email ? `(${c.email})` : ''}
                    </option>
                  ))}
                </Select>
              ) : (
                <div className="p-4 rounded-xl bg-teal-50/50 dark:bg-teal-950/30 border border-teal-100 dark:border-teal-900/60 space-y-3 animate-fade-in">
                  <Input
                    label="Nombre Completo o Razón Social"
                    name="customer_name"
                    placeholder="Empresa / Contacto"
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    error={errors.customer_name?.[0]}
                    required={formData.is_new_customer}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      label="Correo Electrónico"
                      type="email"
                      name="customer_email"
                      placeholder="cliente@email.com"
                      value={formData.customer_email}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="Dirección Principal"
                      name="customer_address1"
                      placeholder="Av. Principal #456"
                      value={formData.customer_address1}
                      onChange={handleInputChange}
                      error={errors.customer_address1?.[0]}
                      required={formData.is_new_customer}
                    />
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Dynamic Items Card */}
        <Card>
          <CardHeader actions={
            <Button type="button" variant="secondary" size="sm" icon={Plus} onClick={addItemRow}>
              Agregar Concepto
            </Button>
          }>
            <CardTitle subtitle="Artículos facturados y deducción automática de inventario">
              Partidas y Conceptos
            </CardTitle>
          </CardHeader>

          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4 w-48">Producto Inventario</th>
                    <th className="py-3 px-4">Descripción del Concepto</th>
                    <th className="py-3 px-4 w-24">Cantidad</th>
                    <th className="py-3 px-4 w-32">Precio Unit.</th>
                    <th className="py-3 px-4 w-32 text-right">Subtotal</th>
                    <th className="py-3 px-4 w-12 text-center"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {items.map((item, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                      <td className="py-2.5 px-4">
                        <select
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-1 focus:ring-brand-500 focus:outline-none"
                          value={item.product_id}
                          onChange={(e) => handleItemChange(idx, 'product_id', e.target.value)}
                        >
                          <option value="">(Manual sin inventario)</option>
                          {products.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name} (Stock: {p.stock})
                            </option>
                          ))}
                        </select>
                      </td>

                      <td className="py-2.5 px-4">
                        <input
                          type="text"
                          required
                          placeholder="Descripción del concepto o servicio..."
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-1 focus:ring-brand-500 focus:outline-none"
                          value={item.description}
                          onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                        />
                      </td>

                      <td className="py-2.5 px-4">
                        <input
                          type="number"
                          step="any"
                          min="0.01"
                          required
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-1 focus:ring-brand-500 focus:outline-none text-right font-mono"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                        />
                      </td>

                      <td className="py-2.5 px-4">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          required
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-1 focus:ring-brand-500 focus:outline-none text-right font-mono font-bold"
                          value={item.price}
                          onChange={(e) => handleItemChange(idx, 'price', e.target.value)}
                        />
                      </td>

                      <td className="py-2.5 px-4 text-right font-black text-slate-900 dark:text-slate-100 font-mono text-sm">
                        {currencySymbol} {Number(item.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>

                      <td className="py-2.5 px-4 text-center">
                        <button
                          type="button"
                          disabled={items.length <= 1}
                          onClick={() => removeItemRow(idx)}
                          className="text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-slate-400 transition-colors p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Total Footer Banner */}
            <div className="p-4 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Total Factura
              </span>
              <span className="text-xl font-black text-slate-900 dark:text-slate-100 font-mono">
                {currencySymbol} {Number(grandTotal).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </CardBody>
        </Card>

        {/* PAYMENTS & ABONOS SECTION (INVOICE EDIT) */}
        {isEdit && (
          <Card>
            <CardHeader
              actions={
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  icon={CreditCard}
                  onClick={openPayModal}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs"
                >
                  + Registrar Abono / Pago
                </Button>
              }
            >
              <CardTitle subtitle="Registra cobros y administra abonos directamente en la factura">
                Gestión de Abonos y Pagos ({payments.length})
              </CardTitle>
            </CardHeader>

            <CardBody className="p-0">
              {/* Financial Metrics Bar */}
              <div className="p-5 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                    <span className="text-[10px] uppercase font-bold text-slate-400">Total Factura</span>
                    <p className="font-mono font-black text-base text-slate-900 dark:text-white mt-0.5">
                      {currencySymbol} {Number(grandTotal).toFixed(2)}
                    </p>
                  </div>

                  <div className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                    <span className="text-[10px] uppercase font-bold text-slate-400">Cobrado (Abonos)</span>
                    <p className="font-mono font-black text-base text-emerald-600 dark:text-emerald-400 mt-0.5">
                      {currencySymbol} {Number(totalPaid).toFixed(2)}
                    </p>
                  </div>

                  <div className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                    <span className="text-[10px] uppercase font-bold text-slate-400">Saldo Pendiente</span>
                    <p className={`font-mono font-black text-base mt-0.5 ${Number(amountPending) > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600'}`}>
                      {currencySymbol} {Number(amountPending).toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div>
                  <div className="flex justify-between text-xs font-semibold text-slate-500 mb-1.5">
                    <span>Progreso del Pago</span>
                    <span>{pctPaid}% completado</span>
                  </div>
                  <div className="w-full h-2.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${pctPaid >= 100 ? 'bg-emerald-500' : 'bg-[#08707A]'}`}
                      style={{ width: `${pctPaid}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Payments List */}
              {payments.length === 0 ? (
                <div className="p-8 text-center space-y-3">
                  <p className="text-xs text-slate-400">No se han registrado abonos para esta factura aún.</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    icon={Plus}
                    onClick={openPayModal}
                    className="mx-auto"
                  >
                    Registrar Primer Abono
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {payments.map((p) => (
                    <div key={p.id} className="p-4 flex items-center justify-between gap-4 text-xs hover:bg-slate-50/60 dark:hover:bg-slate-800/30 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                          <DollarSign className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold font-mono text-slate-900 dark:text-slate-100 text-sm">
                              {currencySymbol} {Number(p.amount).toFixed(2)}
                            </span>
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-teal-50 text-[#08707A] dark:bg-teal-950/60 dark:text-teal-300 border border-teal-200 dark:border-teal-800">
                              {p.method || 'Efectivo'}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            {new Date(p.created_at).toLocaleString()} {p.description ? `· ${p.description}` : ''}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setPaymentToDelete(p);
                          setDeletePaymentConfirmOpen(true);
                        }}
                        className="text-slate-400 hover:text-red-600 transition-colors p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/40"
                        title="Eliminar este abono"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* Notes & Submit */}
        <Card>
          <CardBody className="space-y-4">
            <Textarea
              label="Notas, Cuentas Bancarias e Instrucciones de Pago"
              name="notes"
              placeholder="Ej: Transferir a la cuenta corriente del Banco XYZ #000-111-222..."
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
            />

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              <Link to="/invoices">
                <Button variant="secondary" disabled={submitting}>
                  Cancelar
                </Button>
              </Link>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                icon={Save}
                isLoading={submitting}
                className="bg-[#08707A] hover:bg-[#065B63]"
              >
                {isEdit ? 'Guardar Cambios de Factura' : 'Emitir Factura'}
              </Button>
            </div>
          </CardBody>
        </Card>
      </form>

      {/* Record Payment / Abono Modal in Edit Mode */}
      {isEdit && (
        <Modal
          isOpen={payModalOpen}
          onClose={() => !paySubmitting && setPayModalOpen(false)}
          title="Registrar Abono o Pago"
          subtitle={`Factura #${invoiceNumber || id} · Saldo pendiente: ${currencySymbol} ${Number(amountPending).toFixed(2)}`}
        >
          <form onSubmit={handleRecordPayment} className="p-6 space-y-4">
            <Input
              label="Monto a Cobrar / Abonar"
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
              required
              autoFocus
            />

            <Select
              label="Método de Pago"
              value={payMethod}
              onChange={(e) => setPayMethod(e.target.value)}
            >
              <option value="Cash">Efectivo</option>
              <option value="Bank Transfer">Transferencia Bancaria</option>
              <option value="Credit Card">Tarjeta de Crédito / Débito</option>
              <option value="PayPal">PayPal</option>
              <option value="Check">Cheque</option>
              <option value="Other">Otro</option>
            </Select>

            <Input
              label="Nota / Referencia de Pago (Opcional)"
              placeholder="Ej: Transferencia Banco XYZ Ref #12345"
              value={payDescription}
              onChange={(e) => setPayDescription(e.target.value)}
            />

            <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPayModalOpen(false)}
                disabled={paySubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                isLoading={paySubmitting}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Guardar Abono
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Pending Balance Warning Prompt in Edit Mode */}
      {isEdit && (
        <Modal
          isOpen={pendingWarningOpen}
          onClose={() => setPendingWarningOpen(false)}
          title="Saldo Pendiente Requerido"
          subtitle={`Factura #${invoiceNumber || id}`}
        >
          <div className="p-6 space-y-4">
            <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="text-xs space-y-1">
                <p className="font-bold text-amber-900 dark:text-amber-200">
                  La factura no puede marcarse como PAGADA directamente.
                </p>
                <p className="text-amber-700 dark:text-amber-300">
                  Existe un saldo pendiente de{' '}
                  <strong>
                    {currencySymbol} {Number(amountPending).toFixed(2)}
                  </strong>
                  . El sistema requiere que los abonos registrados cubran el total de la factura antes de pasar a estado Pagada.
                </p>
              </div>
            </div>

            <p className="text-xs text-slate-600 dark:text-slate-300">
              ¿Deseas registrar un abono por el monto restante de{' '}
              <strong className="text-slate-900 dark:text-white">
                {currencySymbol} {Number(amountPending).toFixed(2)}
              </strong>{' '}
              para marcarla automáticamente como Pagada?
            </p>

            <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
              <Button variant="secondary" onClick={() => setPendingWarningOpen(false)}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                className="bg-emerald-600 hover:bg-emerald-700"
                icon={CreditCard}
                onClick={() => {
                  setPendingWarningOpen(false);
                  openPayModal();
                }}
              >
                Registrar Saldo Restante
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Payment Confirm in Edit Mode */}
      {isEdit && (
        <ConfirmDialog
          isOpen={deletePaymentConfirmOpen}
          onClose={() => setDeletePaymentConfirmOpen(false)}
          onConfirm={handleDeletePayment}
          title="¿Eliminar registro de abono?"
          message={`Se eliminará el abono de ${currencySymbol} ${Number(paymentToDelete?.amount || 0).toFixed(2)}. El saldo pendiente de la factura se recalculará automáticamente.`}
          confirmText="Eliminar Abono"
        />
      )}
    </div>
  );
};
